import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";

/**
 * AUTH CALLBACK
 *
 * Landing page for email confirmation / magic links.
 * Supabase hydrates the session from the URL (hash tokens or ?code=),
 * then we route the user onward:
 *   - profile incomplete → /profile-setup
 *   - profile complete   → /
 *   - no session         → /auth
 */
const AuthCallback = () => {
  const navigate = useNavigate();
  const [message, setMessage] = useState("Signing you in…");

  useEffect(() => {
    // A confirmed user should never be bounced back to the splash screen.
    localStorage.setItem("aura-splash-seen", "true");

    let cancelled = false;

    const resolve = async () => {
      // Exchange PKCE code if present (hash tokens are handled automatically).
      const url = new URL(window.location.href);
      const code = url.searchParams.get("code");
      if (code) {
        try {
          await supabase.auth.exchangeCodeForSession(code);
        } catch {
          /* fall through to session check below */
        }
      }

      // Give the client a moment to persist the session from the URL.
      for (let attempt = 0; attempt < 10; attempt++) {
        const { data } = await supabase.auth.getSession();
        if (cancelled) return;

        if (data.session?.user) {
          const { data: profile } = await supabase
            .from("profiles")
            .select("onboarding_completed")
            .eq("user_id", data.session.user.id)
            .maybeSingle();

          if (cancelled) return;
          navigate(profile?.onboarding_completed ? "/" : "/profile-setup", {
            replace: true,
          });
          return;
        }

        await new Promise((r) => setTimeout(r, 300));
      }

      if (cancelled) return;
      setMessage("We couldn't complete sign in. Redirecting…");
      navigate("/auth", { replace: true });
    };

    resolve();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-background px-6 text-center">
      <Loader2 className="h-6 w-6 animate-spin text-primary" />
      <p className="text-muted-foreground">{message}</p>
    </div>
  );
};

export default AuthCallback;
